import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import NumberFormat from "react-number-format";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Grid,
  Button,
  Select,
  IconButton,
  Box,
  Icon,
  Text,
  CircularProgress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";

import AddSection from "Components/AddSection";
import Swal from "sweetalert2";
import MaterialTable from "material-table";
import { useState, useEffect } from "react";
import { MdModeEdit } from "react-icons/md";
import api from "services/api";
import { tableIcons } from "utils/tableIcons";
import { sortDESC } from "utils/sort";
import { cnpjMask } from "utils/addZeroes";

const schema = Yup.object().shape({
  nm_entidade: Yup.string().required("Preencha o nome da Pessoa Jurídica"),
  cnpj_entidade: Yup.string()
    .required("Preencha o CNPJ da Pessoa Jurídica")
    .matches(
      /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
      "O CNPJ deve ter 14 dígitos",
    ),
  tipos_entidade: Yup.string().required("Preencha o tipo da Pessoa Jurídica"),
});

interface Entidade {
  id: number;
  nm_entidade: string;
  cnpj_entidade: string;
  tipos_entidade: string;
}

interface TipoEntidade {
  id: number;
  tp_entidade: string;
}

const defaultValues = {
  nm_entidade: "",
  cnpj_entidade: "",
  tipos_entidade: "",
};

const PessoasJuridicas = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentEditing, setCurrentEditing] = useState<null | number>(null);
  const [inputEditingName, setInputEditingName] = useState("");
  const [inputEditingCNPJ, setInputEditingCNPJ] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [entidades, setEntidades] = useState<Entidade[]>([]);
  const [tipoEntidades, setTipoEntidades] = useState<TipoEntidade[]>([]);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<Entidade>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const onSubmit = async (values: Entidade) => {
    const data = {
      nm_entidade: values.nm_entidade,
      cnpj_entidade: values.cnpj_entidade
        .replace(".", "")
        .replace(".", "")
        .replace("/", "")
        .replace("-", ""),
      tipos_entidade: values.tipos_entidade,
    };

    await api
      .post("/entidades", data)
      .then((res) => {
        setEntidades([...entidades, res.data].sort(sortDESC));
        reset();
        Swal.fire({
          position: "top",
          icon: "success",
          title: "Pessoa Jurídica cadastrada com sucesso",
          showConfirmButton: false,
          timer: 3000,
        });
      })
      .catch((error) => {
        console.log("erro: ", error);
        Swal.fire({
          icon: "warning",
          title:
            "Algo deu errado! Entre em contato com o administrador do sistema",
          showConfirmButton: true,
          confirmButtonText: `Ok`,
        });
      });
  };

  async function handleEditEntity() {
    Swal.fire({
      title: "Tem certeza que deseja editar essa Pessoa Jurídica?",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      setEditLoading(true);
      try {
        const response = await api.put(`/entidades/${currentEditing}`, {
          nm_entidade: inputEditingName,
          cnpj_entidade: parseInt(
            inputEditingCNPJ
              .replace(".", "")
              .replace(".", "")
              .replace("/", "")
              .replace("-", ""),
          ),
          // tipos_entidade: inputEditingEntityType,
        });

        const updatedEntities = entidades.map((entidade) => {
          if (entidade.id === response.data.id) {
            return response.data;
          }

          return entidade;
        });

        setEntidades(updatedEntities);
        setInputEditingName("");
        setInputEditingCNPJ("");
        setCurrentEditing(null);
        setInputEditingName("");
        setInputEditingCNPJ("");
        setEditLoading(false);
        Swal.fire({
          position: "center",
          icon: "success",
          title: "Pessoa Jurídica editada com sucesso",
          showConfirmButton: false,
          timer: 3000,
        });
      } catch (err) {
        console.log(err);
        setEditLoading(false);
        Swal.fire({
          icon: "error",
          title: "Erro ao editar",
          showConfirmButton: true,
          confirmButtonText: `Ok`,
        });
      }
    });
  }

  useEffect(() => {
    async function getEntidades() {
      try {
        const response = await api.get("/entidades?_sort=id:DESC");
        setEntidades(response.data);
      } catch (err) {
        console.log(err);
        setPageLoading(false);
        Swal.fire({
          icon: "error",
          title: "Erro ao listar as pessoas jurídicas",
          showConfirmButton: true,
          confirmButtonText: `Ok`,
        });
      }
    }
    async function getTipoEntidades() {
      try {
        const response = await api.get("/tipos-entidades");
        setTipoEntidades(response.data);

        setPageLoading(false);
      } catch (err) {
        console.log(err);
        setPageLoading(false);
        Swal.fire({
          icon: "error",
          title: "Erro ao listar tipos-entidades!",
          showConfirmButton: true,
          confirmButtonText: `Ok`,
        });
      }
    }

    getEntidades();
    getTipoEntidades();
  }, []);

  if (pageLoading) {
    return (
      <Box
        as="section"
        display="flex"
        w="100%"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress isIndeterminate color="blue.200" />
      </Box>
    );
  }

  return (
    <>
      <Box
        as="section"
        display="flex"
        flexDir="column"
        w="100%"
        maxHeight="calc(100vh - 72px)"
        overflow="auto"
        pb="4"
      >
        <AddSection titleButton="Cadastrar Pessoa Jurídica" scrollBehavior="">
          <form
            onSubmit={handleSubmit(onSubmit)}
            onReset={(event) => {
              event.preventDefault();
              reset(defaultValues);
            }}
          >
            <Grid gap={6}>
              <FormControl
                isInvalid={!!errors?.nm_entidade?.message}
                errortext={errors?.nm_entidade?.message}
                isRequired
              >
                <FormLabel htmlFor="nm_entidade">Nome</FormLabel>
                <Input
                  id="nm_entidade"
                  type="text"
                  {...register("nm_entidade")}
                />

                <FormErrorMessage>
                  {errors?.nm_entidade?.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={!!errors?.cnpj_entidade?.message}
                errortext={errors?.cnpj_entidade?.message}
                isRequired
              >
                <FormLabel htmlFor="cnpj_entidade">CNPJ</FormLabel>
                <Controller
                  name="cnpj_entidade"
                  control={control}
                  render={({ field }) => (
                    <NumberFormat
                      {...field}
                      format="##.###.###/####-##"
                      mask="_"
                      customInput={Input}
                    />
                  )}
                />
                <FormErrorMessage>
                  {errors?.cnpj_entidade?.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={!!errors?.tipos_entidade?.message}
                errortext={errors?.tipos_entidade?.message}
                isRequired
              >
                <FormLabel htmlFor="tipos_entidade">
                  Tipo da Pessoa Jurídica
                </FormLabel>
                <Select
                  placeholder="Selecione o tipo"
                  id="tipos_entidade"
                  type="text"
                  {...register("tipos_entidade")}
                >
                  {tipoEntidades &&
                    tipoEntidades.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.tp_entidade}
                      </option>
                    ))}
                </Select>

                <FormErrorMessage>
                  {errors?.tipos_entidade?.message}
                </FormErrorMessage>
              </FormControl>

              <Button
                isLoading={isSubmitting}
                type="submit"
                colorScheme="blue"
                disabled={
                  !!errors.nm_entidade ||
                  !!errors.cnpj_entidade ||
                  !!errors.tipos_entidade
                }
              >
                Salvar
              </Button>
            </Grid>
          </form>
        </AddSection>
        <Box as="form" paddingInline="4">
          <MaterialTable
            icons={tableIcons}
            title="Pessoas Jurídicas"
            data={entidades}
            localization={{
              body: {
                emptyDataSourceMessage: "Nenhum dado cadastrado.",
              },
              pagination: {
                labelDisplayedRows: "{from}-{to} de {count}",
                labelRowsSelect: "linhas",
                previousTooltip: "Página anterior",
                nextTooltip: "Próxima página",
                firstTooltip: "Primeira página",
                lastTooltip: "Última página",
              },
              toolbar: {
                searchPlaceholder: "Buscar",
                exportTitle: "Exportar",
                exportAriaLabel: "Exportar",
                exportPDFName: "Exportar como PDF",
              },
            }}
            options={{
              emptyRowsWhenPaging: false,
              tableLayout: "auto",
              exportButton: {
                pdf: true,
                csv: false,
              },
            }}
            columns={[
              {
                title: "Nome",
                field: "nm_entidade",
                cellStyle: {
                  width: "32%",
                },
                render: (element) => {
                  return <Text as="p">{element.nm_entidade}</Text>;
                },
              },
              {
                title: "CNPJ",
                field: "cnpj_entidade",
                cellStyle: {
                  width: "32%",
                },
                render: (element) => {
                  return (
                    <Text as="p">{cnpjMask(element.cnpj_entidade, 14)}</Text>
                  );
                },
              },
              {
                title: "Tipo",
                field: "tipos_entidade.tp_entidade",
                cellStyle: {
                  width: "32%",
                },
              },
              {
                title: "Editar",
                cellStyle: {
                  width: "4%",
                },
                render: (element) => {
                  return (
                    <IconButton
                      aria-label="Editar"
                      icon={<Icon as={MdModeEdit} />}
                      fontSize="24"
                      variant="unstyled"
                      onClick={() => {
                        onOpen();
                        setCurrentEditing(element.id);
                        setInputEditingName(element.nm_entidade);
                        setInputEditingCNPJ(
                          cnpjMask(element.cnpj_entidade, 14),
                        );
                      }}
                    />
                  );
                },
              },
            ]}
          ></MaterialTable>
        </Box>
      </Box>
      {currentEditing && (
        <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
          <ModalOverlay backgroundColor="blackAlpha.500" />
          <ModalContent
            paddingBottom="4"
            marginLeft="15px"
            marginRight="15px"
            marginTop="80px"
          >
            <ModalHeader>Editar Pessoa Jurídica</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Grid gap={6}>
                <FormControl
                  isInvalid={inputEditingName?.length < 1 ? true : false}
                  isRequired
                >
                  <FormLabel htmlFor="nome_edit">Nome</FormLabel>
                  <Input
                    id="nome_edit"
                    name="nome_edit"
                    type="text"
                    onChange={(e) => {
                      setInputEditingName(e.target.value);
                    }}
                    defaultValue={inputEditingName}
                  />
                  <FormErrorMessage>
                    Esse campo não pode ficar vazio
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={
                    parseInt(
                      inputEditingCNPJ
                        .replace(".", "")
                        .replace(".", "")
                        .replace("/", "")
                        .replace("-", ""),
                    ).toString().length < 14
                      ? true
                      : false
                  }
                  isRequired
                >
                  <FormLabel htmlFor="cnpj_edit">CNPJ</FormLabel>
                  <NumberFormat
                    customInput={Input}
                    format="##.###.###/####-##"
                    variant="flushed"
                    defaultValue={inputEditingCNPJ}
                    onChange={(e) => {
                      setInputEditingCNPJ(e.target.value);
                    }}
                  />
                  <FormErrorMessage>
                    O CNPJ deve ter 14 dígitos
                  </FormErrorMessage>
                </FormControl>

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  gridColumnGap="10px"
                >
                  <Button
                    width="130px"
                    isLoading={editLoading}
                    onClick={handleEditEntity}
                    colorScheme="blue"
                    disabled={
                      parseInt(
                        inputEditingCNPJ
                          .replace(".", "")
                          .replace(".", "")
                          .replace("/", "")
                          .replace("-", ""),
                      ).toString().length < 14 || inputEditingName?.length < 1
                    }
                  >
                    Salvar
                  </Button>
                  <Button
                    width="130px"
                    colorScheme="facebook"
                    onClick={() => {
                      onClose();
                      setInputEditingName("");
                      setInputEditingCNPJ("");
                      setCurrentEditing(null);
                      setEditLoading(false);
                    }}
                  >
                    Cancelar
                  </Button>
                </Box>
              </Grid>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default PessoasJuridicas;
